/**
 * Home Component - Refactored
 * Main application page for BIM validation and chat
 */

import React, { useState, useEffect, useRef, FormEvent } from 'react';
import { OptionOnSelectData, SelectionEvents } from '@fluentui/react-components';
import { useAuth } from 'react-oidc-context';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { ChatMessageRole } from 'types/types';

// Components
import FeedbackDialog from 'components/FeedbackDialog';
import { ValidationMode } from 'components/ValidationMode';
import { ChatMode } from 'components/ChatMode';
import { ModeSwitch } from 'components/ModeSwitch';
import { FeedbackButtons } from 'components/FeedbackButtons';
import { PromptArea } from 'components/PromptArea';
import { Tutorial, getHomeTutorialSteps } from 'components/Tutorial';
import Layout from 'components/layout';

// Hooks
import { useChatHistory } from 'hooks/useChatHistory';
import { useStreamingRequest } from 'hooks/useStreamingRequest';
import { usePersistedSettings } from 'hooks/usePersistedSettings';
import { useMessageHandler } from 'hooks/useMessageHandler';
import { useFeedback } from 'hooks/useFeedback';
import { useWebSocketManager } from 'hooks/useWebSocketManager';

// Services & Utils
import { submitChat, getAvailableCountries, getLegislationDocuments, LegislationDocument } from 'services/apiService';
import { isCanonicalElementContent, submitElementValidation } from 'services/validationRouting';
import { FindingEvent } from 'types/findings';
import { updateStreamingContent, setupArticles } from 'utils/domHelpers';
import { notifyHost, requestHostVersion, requestToolsList } from 'utils/hostNotification';
import { getApiHost, STORAGE_KEYS } from 'constants/config';

import './home.css';

interface HomeProps {
  dispatchToast: (content: React.ReactElement, options?: { intent?: 'success' | 'error' | 'warning' | 'info'; timeout?: number }) => void;
}

export function Home({ dispatchToast }: HomeProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const auth = useAuth();
  const [searchParams] = useSearchParams();

  // Authentication/environment consistency (redirecting to /login when
  // unauthenticated, preserving the full query string) is centrally
  // handled by useAuthEnvironmentGuard in App.tsx for every route.

  // API configuration
  const apiAddress = searchParams.get("address");
  const apiHost = getApiHost(apiAddress);
  const token = auth.user?.access_token;

  // Persisted settings
  const { 
    language, 
    legislationCountry, 
    validator,
    chatMode,
    debugMode,
    setLanguage,
    setLegislationCountry,
    setValidator,
    setChatMode
  } = usePersistedSettings();

  // Message handling from BIM software
  const {
    elementId,
    source,
    country,
    elementType,
    elementTypeError,
    content,
    isDeterminingElementType,
    hostVersion,
    toolsList
  } = useMessageHandler(apiHost, token, searchParams.get("debug") === "true");

  // Chat history
  const { chatHistory, addToHistory, updateLastEntry, clearHistory } = useChatHistory();

  // Streaming request management
  const {
    isPending,
    isStreaming,
    abortController,
    streamingContentRef,
    startStreaming,
    stopStreaming,
    finishStreaming,
    setIsPending,
    resetStreamingContent
  } = useStreamingRequest();

  // Mode and UI state
  const [validationMode, setValidationMode] = useState<boolean>(true);
  const [prompt, setPrompt] = useState<string>("");
  const [lastRequest, setLastRequest] = useState<string>('');
  const [availableCountries, setAvailableCountries] = useState<string[]>([]);
  const [showTutorial, setShowTutorial] = useState<boolean>(false);
  const [tutorialDemoMode, setTutorialDemoMode] = useState<boolean>(false);
  const [canonicalFindings, setCanonicalFindings] = useState<FindingEvent[]>([]);
  
  // Legislation documents state
  const [legislationDocuments, setLegislationDocuments] = useState<LegislationDocument[]>([]);
  const [excludedDocuments, setExcludedDocuments] = useState<string[]>(() => {
    const stored = localStorage.getItem(STORAGE_KEYS.EXCLUDED_DOCUMENTS);
    return stored ? JSON.parse(stored) : [];
  });
  const [isLoadingDocuments, setIsLoadingDocuments] = useState<boolean>(false);
  
  // Tutorial demo data
  const tutorialElementType = "Window";
  const tutorialCountry = "Ireland";
  
  // Feedback dialog
  const {
    openFeedbackDialog,
    feedbackType,
    setFeedbackContent,
    setOpenFeedbackDialog,
    openPositiveFeedback,
    openNegativeFeedback,
    handleSubmitFeedback
  } = useFeedback(apiAddress, token);

  // Refs
  const contentRef = useRef<HTMLDivElement>(null);
  const lastValidationResponse = useRef('');
  const lastChatResponse = useRef('');
  const elementContents = useRef<{ [elementId: string]: string }>({});

  // Check if debug mode is enabled for WebSocket
  const isDebugMode = searchParams.get("debug") === "true";
  const isWsDebugEnabled = searchParams.get("ws") === "true";

  // WebSocket connection and management
  const { isWebSocketConnected, debugMessages, clearDebugMessages } = useWebSocketManager({
    apiHost,
    token,
    isDebugMode,
    isWsDebugEnabled,
    validationMode,
    contentRef
  });

  // Request host version on mount
  useEffect(() => {
    requestHostVersion();
    requestToolsList();
  }, []);

  // Load available countries on mount
  useEffect(() => {
    async function loadCountries() {
      try {
        const countries = await getAvailableCountries(apiAddress);
        setAvailableCountries(countries);
      } catch (error) {
        console.error('Failed to load countries:', error);
      } finally {
        setIsPending(false);
      }
    }
    loadCountries();
  }, [apiAddress, setIsPending]);

  // Load legislation documents when country changes
  useEffect(() => {
    async function loadLegislationDocuments() {
      if (!country) {
        setLegislationDocuments([]);
        return;
      }
      
      setIsLoadingDocuments(true);
      try {
        const documents = await getLegislationDocuments(country, token, apiAddress);
        setLegislationDocuments(documents);
        
        // Filter out any excluded documents that are no longer available
        const availableDocNames = documents.map(d => d.name);
        const validExcluded = excludedDocuments.filter(name => availableDocNames.includes(name));
        if (validExcluded.length !== excludedDocuments.length) {
          setExcludedDocuments(validExcluded);
          localStorage.setItem(STORAGE_KEYS.EXCLUDED_DOCUMENTS, JSON.stringify(validExcluded));
        }
      } catch (error) {
        console.error('Failed to load legislation documents:', error);
        setLegislationDocuments([]);
      } finally {
        setIsLoadingDocuments(false);
      }
    }
    loadLegislationDocuments();
  }, [country, token, apiAddress, excludedDocuments]);

  // Handle excluded documents change
  const handleExcludedDocumentsChange = (excluded: string[]) => {
    setExcludedDocuments(excluded);
    localStorage.setItem(STORAGE_KEYS.EXCLUDED_DOCUMENTS, JSON.stringify(excluded));
  };

  // Check if first-time user and show tutorial
  useEffect(() => {
    const hasSeenTutorial = localStorage.getItem('bimcomply_tutorial_completed');
    if (!hasSeenTutorial && auth.user) {
      // Add a small delay to ensure the page has fully loaded
      const timer = setTimeout(() => {
        setShowTutorial(true);
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [auth.user]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (abortController) {
        abortController.abort();
      }
    };
  }, [abortController]);

  // Handlers
  function onLanguageSelected(ev: SelectionEvents, data: OptionOnSelectData) {
    setLanguage(data.optionValue ?? "");
  }

  function onLegislationCountrySelected(ev: SelectionEvents, data: OptionOnSelectData) {
    setLegislationCountry(data.optionValue ?? "");
  }

  function onValidatorSelected(ev: SelectionEvents, data: OptionOnSelectData) {
    setValidator(data.optionValue ?? "");
  }

  function onChatModeSelected(ev: SelectionEvents, data: OptionOnSelectData) {
    setChatMode(data.optionValue as 'project' | 'legislation' ?? 'legislation');
  }

  function switchMode() {
    if (abortController) {
      abortController.abort();
      stopStreaming();
    }
    
  lastValidationResponse.current = '';
  resetStreamingContent();
    
    if (contentRef.current) {
      contentRef.current.innerHTML = '';
    }
    
    setPrompt('');
    setCanonicalFindings([]);
    clearHistory();
    clearDebugMessages();
    setLastRequest('');
    setValidationMode(!validationMode);
  }

  function handleTutorialComplete() {
    localStorage.setItem('bimcomply_tutorial_completed', 'true');
    setShowTutorial(false);
    setTutorialDemoMode(false);
    // Clear demo content
    if (contentRef.current) {
      contentRef.current.innerHTML = '';
    }
  }

  function handleTutorialSkip() {
    localStorage.setItem('bimcomply_tutorial_completed', 'true');
    setShowTutorial(false);
    setTutorialDemoMode(false);
    // Clear demo content
    if (contentRef.current) {
      contentRef.current.innerHTML = '';
    }
  }

  function handleShowTutorial() {
    // Restart the tutorial
    setShowTutorial(true);
    setTutorialDemoMode(false);
    setValidationMode(true);
    // Clear any existing content
    if (contentRef.current) {
      contentRef.current.innerHTML = '';
    }
  }

  function showTutorialDemoArticles() {
    console.log('showTutorialDemoArticles called');
    setTutorialDemoMode(true);
    if (contentRef.current) {
      console.log('contentRef.current exists, importing demo articles...');
      // Import and display demo articles
      import('utils/demoArticles').then(({ generateDemoArticles }) => {
        console.log('Demo articles module loaded');
        const demoArticles = generateDemoArticles();
        console.log('Generated', demoArticles.length, 'demo articles');
        
        // Map status to conclusion class names
        const getStatusClass = (status: string) => {
          switch(status) {
            case 'applicable': return 'valid';
            case 'needs-review': return 'undetermined';
            case 'invalid': return 'invalid';
            case 'not-applicable': return 'not-applicable';
            default: return 'undetermined';
          }
        };

        const getStatusText = (status: string) => {
          switch(status) {
            case 'applicable': return 'Valid';
            case 'needs-review': return 'Undetermined';
            case 'invalid': return 'Not Compliant';
            case 'not-applicable': return 'Not Applicable';
            default: return 'Undetermined';
          }
        };
        
        const articlesHTML = `
          <h1>Building Legislation Inspection Report - Windows</h1>
          ${demoArticles.map(article => `
            <div class="article collapsed">
              <h2>${article.reference} - ${article.title}<span class="article-toggle">
                <svg class="chevron-up" width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M4.64645 13.3536C4.45118 13.1583 4.45118 12.8417 4.64645 12.6464L9.64645 7.64645C9.84171 7.45118 10.1583 7.45118 10.3536 7.64645L15.3536 12.6464C15.5488 12.8417 15.5488 13.1583 15.3536 13.3536C15.1583 13.5488 14.8417 13.5488 14.6464 13.3536L10 8.70711L5.35355 13.3536C5.15829 13.5488 4.84171 13.5488 4.64645 13.3536Z" fill="currentColor"></path>
                </svg>
                <svg class="chevron-down" width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M15.3536 7.64645C15.5488 7.84171 15.5488 8.15829 15.3536 8.35355L10.3536 13.3536C10.1583 13.5488 9.84171 13.5488 9.64645 13.3536L4.64645 8.35355C4.45118 8.15829 4.45118 7.84171 4.64645 7.64645C4.84171 7.45118 5.15829 7.45118 5.35355 7.64645L10 12.2929L14.6464 7.64645C14.8417 7.45118 15.1583 7.45118 15.3536 7.64645Z" fill="currentColor"></path>
                </svg>
              </span></h2>
              <div class="article-content">
                <blockquote>
                  <p>${article.content}</p>
                </blockquote>
                <div class="conclusion ${getStatusClass(article.status)}">
                  <h1>Conclusion: ${getStatusText(article.status)}</h1>
                  <p>${article.conclusion}</p>
                </div>
              </div>
            </div>
          `).join('')}
          <h1>Overall Conclusion</h1>
          <div class="overall-conclusion undetermined">
            <h1>Conclusion: Undetermined</h1>
            <p>The element is undetermined. While the window demonstrates compliance with the maximum U-value requirement (0.85 W/m²K vs. 1.4 W/m²K max) and has an acceptable air permeability rating (CLASS 3) as per Technical Guidance Document L, several critical aspects remain undetermined due to missing information. Specifically, data is lacking to assess:</p>
            <ul>
              <li>Compliance with fire escape dimensions and functionality (TGD B).</li>
              <li>Whether the glazing is safety glazing in critical locations (TGD D).</li>
              <li>The need for and provision of guarding or safety restrictors, and their compliance with dimensional limits, especially concerning external ground level (TGD K).</li>
              <li>Details on thermal bridging at junctions and condensation risk (TGD L).</li>
              <li>The window's opening angle to definitively assess purge ventilation area requirements (TGD F).</li>
              <li>Whether the window opens out onto an access route and its proximity to such routes (TGD M).</li>
              <li>The fire resistance rating of the window for "unprotected area" definitions (TGD B).</li>
            </ul>
            <p>Without these details, a comprehensive assessment of the window's full compliance with all applicable building regulations cannot be made.</p>
          </div>
        `;
        console.log('Setting innerHTML with articles HTML');
        contentRef.current!.innerHTML = articlesHTML;
        
        console.log('Calling setupArticles');
        // Setup click handlers for the articles
        setupArticles(contentRef.current!);
        console.log('Articles setup complete');
      });
    } else {
      console.log('contentRef.current is null!');
    }
  }

  function handleClick() {
    if (isStreaming && abortController) {
      stopStreaming();
      return;
    }

    if (validationMode) {
      handleValidationRequest();
    } else {
      handleChatRequest();
    }
  }

  async function handleValidationRequest() {
    const controller = startStreaming();
    lastValidationResponse.current = '';
    elementContents.current = {};

    const isCanonicalRequest = isCanonicalElementContent(content);
    if (!isCanonicalRequest && contentRef.current) {
      contentRef.current.innerHTML = '';
    }

    const request = {
      Source: source,
      Validator: validator,
      Country: country,
      Language: language,
      ElementType: elementType,
      Content: content,
      ...(toolsList && { Tools: toolsList }),
      ...(excludedDocuments.length > 0 && { ExcludedDocuments: excludedDocuments }),
      // Debug Mode is opt-in and OFF by default; only ever attached when the
      // user has explicitly enabled it. The API independently authorizes
      // capture and fails closed, so this flag alone never guarantees capture.
      ...(debugMode && { DebugCapture: true, CorrelationId: crypto.randomUUID() })
    };

    try {
      setCanonicalFindings([]);
      await submitElementValidation(
        request,
        token,
        apiAddress,
        controller.signal,
        {
          onLegacyStream: (chunk) => {
            lastValidationResponse.current += chunk;
            updateStreamingContent(contentRef.current, chunk, streamingContentRef, elementContents);
          },
          findings: {
            onFinding: finding => setCanonicalFindings(previous => [...previous, finding]),
          },
        }
      );

      setLastRequest(JSON.stringify(request));
      notifyHost(elementId, contentRef.current);
    } catch (error: any) {
      if (error.message === 'UNAUTHORIZED') {
        navigate(`/login${location.search}`);
      } else if (error.name !== 'AbortError') {
        alert(error);
        console.error(error);
      }
    } finally {
      finishStreaming();
    }
  }

  async function handleChatRequest() {
    const controller = startStreaming();
    lastChatResponse.current = '';

    const useRag = chatMode === 'legislation';
    
    const request: any = {
      Validator: validator,
      Language: language,
      Messages: chatHistory,
      Content: prompt,
      UseRag: useRag,
      ...(toolsList && { Tools: toolsList })
    };

    // Only include Country if using RAG (legislation mode)
    if (useRag) {
      request.Country = legislationCountry;
    }

    addToHistory(prompt, ChatMessageRole.User);
    setPrompt('');

    try {
      await submitChat(
        request,
        token,
        apiAddress,
        controller.signal,
        (chunk) => {
          // Add assistant message on first chunk received
          if (lastChatResponse.current === '') {
            addToHistory('', ChatMessageRole.Model);
          }
          lastChatResponse.current += chunk;
          updateLastEntry(lastChatResponse.current);
        }
      );
    } catch (error: any) {
      if (error.message === 'UNAUTHORIZED') {
        navigate(`/login${location.search}`);
      } else if (error.name !== 'AbortError') {
        alert(error);
        console.error(error);
      }
    } finally {
      finishStreaming();
    }
  }

  // Wrap handleSubmitFeedback to include additional request data
  const onSubmitFeedback = (event: FormEvent) => {
    return handleSubmitFeedback(event, {
      Source: source,
      Validator: validator,
      Country: country,
      Language: language,
      ElementType: elementType,
      Input: lastRequest,
      Output: lastValidationResponse.current
    });
  };

  function getButtonTooltip(): string | undefined {
    if (isStreaming) {
      return "Click to stop the streaming response";
    }
    if (isPending) {
      return "An action is already in Progress. Please Wait!";
    }
    if (!language) {
      return "Please select a Language!";
    }
    if (!validator) {
      return "Please select a Validator!";
    }

    if (validationMode) {
      if (elementTypeError) {
        return elementTypeError;
      }
      if (!availableCountries.includes(country)) {
        return `Please confirm the address in the Project and make sure it's in one of the available countries: ${availableCountries}`;
      }
      if (!content || !source || !elementType) {
        return "Please select an Element!";
      }
    } else if (!validationMode) {
      if (!prompt) {
        return "Please enter a question!";
      }
      if (chatMode === 'legislation' && !legislationCountry) {
        return "Please select a Legislation!";
      }
    }

    return undefined;
  }

  const isSubmitDisabled = !isStreaming && (
    (validationMode && !tutorialDemoMode && (!!elementTypeError || isDeterminingElementType || !content || !source || !elementType || !language || !availableCountries.includes(country))) ||
    (validationMode && tutorialDemoMode && false) || // Never disabled in tutorial demo mode
    (!validationMode && (!prompt || !language || (chatMode === 'legislation' && !legislationCountry))) ||
    !validator ||
    (isPending && !tutorialDemoMode)
  );

  return (
    <Layout onShowTutorial={handleShowTutorial} isWebSocketConnected={isWebSocketConnected} hostVersion={hostVersion}>
      {showTutorial && (
        <Tutorial
          steps={getHomeTutorialSteps(
            () => setValidationMode(true),
            () => setValidationMode(false),
            () => setTutorialDemoMode(true),
            showTutorialDemoArticles
          )}
          onComplete={handleTutorialComplete}
          onSkip={handleTutorialSkip}
        />
      )}

      <FeedbackDialog 
        feedbackType={feedbackType} 
        handleSubmitFeedback={onSubmitFeedback}
        openFeedbackDialog={openFeedbackDialog} 
        setFeedbackContent={setFeedbackContent}
        setOpenFeedbackDialog={setOpenFeedbackDialog} 
      />

      <ValidationMode 
        isActive={validationMode} 
        contentRef={contentRef} 
        legislationDocuments={legislationDocuments}
        excludedDocuments={excludedDocuments}
        onExcludedDocumentsChange={handleExcludedDocumentsChange}
        isLoadingDocuments={isLoadingDocuments}
        canonicalFindings={canonicalFindings}
      />
      <ChatMode isActive={!validationMode} chatHistory={chatHistory} debugMessages={debugMessages} onCopyToPrompt={setPrompt} dispatchToast={dispatchToast} />

      <div className="prompt">
        <div className="feedback-container">
          <ModeSwitch
            isPending={isPending}
            isDeterminingElementType={isDeterminingElementType}
            onSwitchMode={switchMode}
          />
          <FeedbackButtons
            hasLastRequest={lastRequest !== ''}
            onThumbsUp={openPositiveFeedback}
            onThumbsDown={openNegativeFeedback}
          />
        </div>
        
        <PromptArea
          validationMode={validationMode}
          elementType={tutorialDemoMode ? tutorialElementType : elementType}
          elementTypeError={tutorialDemoMode ? '' : elementTypeError}
          country={tutorialDemoMode ? tutorialCountry : country}
          prompt={prompt}
          onPromptChange={setPrompt}
          legislationCountry={legislationCountry}
          chatMode={chatMode}
          validator={validator}
          language={language}
          onValidatorSelected={onValidatorSelected}
          onLanguageSelected={onLanguageSelected}
          onLegislationCountrySelected={onLegislationCountrySelected}
          onChatModeSelected={onChatModeSelected}
          onSubmit={handleClick}
          isSubmitDisabled={isSubmitDisabled}
          submitTooltip={getButtonTooltip()}
          isStreaming={isStreaming}
        />
      </div>
    </Layout>
  );
}

export default Home;
